import classnames from 'classnames'
import { FieldValidator, useField } from 'formik'
import React, { ComponentPropsWithoutRef, useCallback, useRef, useState } from 'react'
import { format } from 'date-fns'

import Field from '../Field/Field'
import FieldDropdown from '../FieldDropdown/FieldDropdown'
import FieldError from '../FieldError/FieldError'

import styles from './TextInput.module.scss'

type TextInputProps = {
  type?: 'text' | 'password' | 'email' | 'date' | 'number' | 'datetime-local' | 'url'
  placeholder?: string
  label?: string
  hideLabel?: boolean
  omitErrorDisplay?: boolean
  children?: React.ReactNode
  dropdown?: React.ReactNode
  dropdownOpen?: boolean
  after?: React.ReactNode
  className?: string
  touched?: boolean
  error?: string
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'children'>

const TextInput = React.memo(
  React.forwardRef(function TextInput(
    {
      type,
      placeholder,
      label,
      hideLabel,
      omitErrorDisplay,
      dropdown,
      dropdownOpen,
      after,
      className,
      touched,
      error,
      ...componentProps
    }: TextInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) {
    // Focus detection (for dropdown visibility)
    const [hasFocus, setHasFocus] = useState(false)

    const handleFocus = useCallback<React.FocusEventHandler<HTMLDivElement>>((e) => {
      setHasFocus(e.currentTarget.contains(e.target))
    }, [])
    const handleBlur = useCallback<React.FocusEventHandler<HTMLDivElement>>((e) => {
      setHasFocus(e.currentTarget.contains(e.relatedTarget))
    }, [])

    const fieldRef = useRef<HTMLDivElement | null>(null)

    return (
      <div onFocus={handleFocus} onBlur={handleBlur} className={classnames(styles.TextInput, className)}>
        <Field
          ref={fieldRef}
          label={hideLabel ? undefined : label}
          disabled={componentProps.disabled}
          hasError={Boolean(touched && error)}
        >
          <input
            ref={ref}
            size={1}
            placeholder={hideLabel ? placeholder || label : placeholder}
            type={type || 'text'}
            aria-label={label}
            {...componentProps}
            className={styles.input}
          />
          {after}
        </Field>
        <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} />
        <FieldDropdown
          open={Boolean(dropdown) && (dropdownOpen !== undefined ? dropdownOpen : hasFocus)}
          anchor={fieldRef.current}
        >
          {dropdown}
        </FieldDropdown>
      </div>
    )
  })
)

export default TextInput

type TextInputFormikProps = {
  name: string
  validate?: FieldValidator
} & ComponentPropsWithoutRef<typeof TextInput>

export const TextInputFormik = React.memo(function TextInputFormik({
  name,
  validate,
  ...componentProps
}: TextInputFormikProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const doValidate = useCallback(
    (value?: any) => {
      if (!inputRef.current?.validity.valid) {
        return inputRef.current?.validationMessage
      }
      if (validate) {
        return validate(value)
      }
    },
    [inputRef, validate]
  )

  const [field, meta, helpers] = useField({
    name,
    validate: doValidate,
    ...componentProps,
  })

  const value =
    componentProps.type === 'datetime-local'
      ? field.value
        ? format(field.value, `yyyy-MM-dd'T'HH:mm:ss`)
        : ''
      : componentProps.type === 'date'
      ? field.value
        ? format(field.value, `yyyy-MM-dd`)
        : ''
      : field.value || ''

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (componentProps.type === 'datetime-local') {
        helpers.setValue(e.target.value ? new Date(e.target.value) : null, true)
      } else if (componentProps.type === 'date') {
        helpers.setValue(e.target.value ? new Date(e.target.value) : null, true)
      } else {
        return field.onChange(e)
      }
    },
    [field.onChange, componentProps.type, helpers.setValue]
  )

  return (
    <TextInput
      ref={inputRef}
      touched={meta.touched}
      error={meta.error}
      {...componentProps}
      {...field}
      value={value}
      onChange={onChange}
    />
  )
})
